import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Play, 
  CheckCircle, 
  RefreshCw, 
  Clock, 
  Languages,
  FileText,
  Target,
  AlertTriangle,
  Zap
} from 'lucide-react';
import { PhaseBasedTranslationEnforcer, PhaseExecutionResult, GlobalPhaseResult } from '@/utils/validation/PhaseBasedTranslationEnforcer';

interface ExecutionState {
  isRunning: boolean;
  currentPhase: number;
  progress: number;
  results: GlobalPhaseResult | null;
  error: string | null;
  phaseDetails: PhaseExecutionResult[];
}

const PhaseBasedTranslationPage = () => {
  const [executionState, setExecutionState] = useState<ExecutionState>({
    isRunning: false,
    currentPhase: 0,
    progress: 0,
    results: null,
    error: null,
    phaseDetails: []
  });

  const phases = PhaseBasedTranslationEnforcer.getPhaseExecutionPlan();

  const handleStartExecution = async () => {
    setExecutionState(prev => ({
      ...prev,
      isRunning: true,
      progress: 0,
      currentPhase: 1,
      error: null,
      results: null,
      phaseDetails: []
    }));

    try {
      // Simulate phase progression
      const progressInterval = setInterval(() => {
        setExecutionState(prev => {
          if (prev.progress < 90) {
            const newProgress = prev.progress + Math.random() * 8;
            const newPhase = Math.min(Math.ceil(newProgress / 12.5), 8);
            
            return {
              ...prev,
              progress: newProgress,
              currentPhase: newPhase
            };
          }
          return prev;
        });
      }, 1000);

      const results = await PhaseBasedTranslationEnforcer.executeAllPhases();
      
      clearInterval(progressInterval);
      
      setExecutionState(prev => ({
        ...prev,
        isRunning: false,
        progress: 100,
        currentPhase: 8,
        results
      }));
    } catch (error) {
      setExecutionState(prev => ({
        ...prev,
        isRunning: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }));
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'destructive';
      case 'high': return 'default';
      case 'medium': return 'secondary';
      default: return 'outline';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'critical': return <AlertTriangle className="h-4 w-4" />;
      case 'high': return <Target className="h-4 w-4" />;
      case 'medium': return <Clock className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Phase-Based Translation Enforcement</title>
        <meta name="description" content="Execute systematic translation enforcement in organized phases" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
              <Languages className="h-8 w-8" />
              Phase-Based Translation Enforcement
            </h1>
            <p className="text-muted-foreground">
              Systematic translation of all hardcoded content organized into manageable phases
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Total Strings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">486</div>
                <div className="text-sm text-muted-foreground">Across 8 phases</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Languages className="h-4 w-4" />
                  Languages
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">6</div>
                <div className="text-sm text-muted-foreground">EN, ES, FR, DE, IT, PT</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Est. Duration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">15-20</div>
                <div className="text-sm text-muted-foreground">Minutes total</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Success Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {executionState.results ? '100%' : '---'}
                </div>
                <div className="text-sm text-muted-foreground">Expected outcome</div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="phases" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="phases">Phase Overview</TabsTrigger>
              <TabsTrigger value="execution">Execute Phases</TabsTrigger>
              <TabsTrigger value="results">Results</TabsTrigger>
            </TabsList>

            <TabsContent value="phases" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {phases.map((phase) => (
                  <Card key={phase.phase} className="relative">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-bold">
                            {phase.phase}
                          </div>
                          {phase.name}
                        </CardTitle>
                        <Badge 
                          variant={getPriorityColor(phase.priority)}
                          className="flex items-center gap-1"
                        >
                          {getPriorityIcon(phase.priority)}
                          {phase.priority.toUpperCase()}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">{phase.description}</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="font-medium">Duration</div>
                          <div className="text-muted-foreground">{phase.estimatedDuration}</div>
                        </div>
                        <div>
                          <div className="font-medium">Strings</div>
                          <div className="text-muted-foreground">{phase.stringCount.toLocaleString()}</div>
                        </div>
                      </div>
                    </CardContent>
                    
                    {executionState.results && executionState.results.phaseResults[phase.phase - 1] && (
                      <div className="absolute top-2 right-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="execution" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Execute Phase-Based Translation
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Run comprehensive translation enforcement organized into systematic phases
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      This will process {phases.reduce((sum, phase) => sum + phase.stringCount, 0).toLocaleString()} strings 
                      across {phases.length} phases. Estimated completion time: 15-20 minutes.
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-4">
                    <Button 
                      onClick={handleStartExecution}
                      disabled={executionState.isRunning}
                      className="flex items-center gap-2"
                      size="lg"
                    >
                      {executionState.isRunning ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      {executionState.isRunning ? 'Executing Phases...' : 'Start Phase Execution'}
                    </Button>
                  </div>

                  {executionState.isRunning && (
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium">
                            Phase {executionState.currentPhase}/8: {phases[executionState.currentPhase - 1]?.name}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {executionState.progress.toFixed(0)}%
                          </span>
                        </div>
                        <Progress value={executionState.progress} className="w-full" />
                      </div>

                      <div className="text-sm text-muted-foreground">
                        Processing: {phases[executionState.currentPhase - 1]?.description}
                      </div>
                    </div>
                  )}

                  {executionState.error && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{executionState.error}</AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="results" className="mt-6">
              {executionState.results ? (
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        Execution Results
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {executionState.results.completedPhases}/{executionState.results.totalPhases}
                          </div>
                          <div className="text-sm text-muted-foreground">Phases Completed</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {executionState.results.totalStringsTranslated.toLocaleString()}
                          </div>
                          <div className="text-sm text-muted-foreground">Strings Translated</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">
                            {executionState.results.totalFilesModified}
                          </div>
                          <div className="text-sm text-muted-foreground">Files Modified</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-orange-600">
                            {(executionState.results.totalDuration / 1000).toFixed(1)}s
                          </div>
                          <div className="text-sm text-muted-foreground">Total Duration</div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <h4 className="font-medium">Phase Results:</h4>
                        {executionState.results.phaseResults.map((result, index) => (
                          <div key={index} className="flex items-center justify-between p-3 border rounded">
                            <div className="flex items-center gap-3">
                              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              </div>
                              <div>
                                <div className="font-medium">Phase {result.phase}: {result.name}</div>
                                <div className="text-sm text-muted-foreground">
                                  {result.stringsTranslated} strings in {(result.duration / 1000).toFixed(1)}s
                                </div>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-green-600">
                              SUCCESS
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Final Coverage by Language</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {Object.entries(executionState.results.finalCoverage).slice(0, 6).map(([lang, coverage]) => (
                          <div key={lang} className="text-center p-3 border rounded">
                            <div className="text-2xl font-bold text-green-600">
                              {coverage.toFixed(1)}%
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {lang.toUpperCase()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card>
                  <CardContent className="text-center py-12">
                    <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No results available. Execute the phases first.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default PhaseBasedTranslationPage;